import { inject, Injectable } from '@angular/core';
import { Ab } from '@df/module-ab/ab.namespace';
import { AbService } from '@df/module-ab/ab.service';
import { BehaviorSubject } from 'rxjs';

interface IRule {
    invert: boolean;
    variant: string;
}
interface ISetup {
    exp: string;
    rules: IRule[];
}
/**
 *
 * Sample rules accepted as string parset by getState method
 *
 * (menu) A'
 * (menu) !B'
 * (menu) A,B'
 *
 */
@Injectable({
    providedIn: 'root'
})
export class AbIfService {
    /**
     * Cache for rule parse
     */
    private setupMap = new Map<string, ISetup | undefined>();

    /**
     * Cache for condition check
     */
    private stateMap = new Map<string, BehaviorSubject<boolean>>();

    private abService = inject(AbService);

    getState(str: string): BehaviorSubject<boolean> {
        if (!this.stateMap.has(str)) {
            if (!str) {
                this.stateMap.set(str, new BehaviorSubject<boolean>(true));
            } else {
                const state$ = new BehaviorSubject<boolean>(false);
                this.stateMap.set(str, state$);
                const setup = this.strToSetup(str);
                if (setup) {
                    this.abService.getSubject(setup.exp).subscribe(val => {
                        const condition = this.checkCondition(val.variant, setup);
                        if (state$.value !== condition) {
                            state$.next(condition);
                        }
                    });
                }
            }
        }
        return this.stateMap.get(str)!;
    }

    private checkCondition(variant: Ab.TVariant, setup: ISetup): boolean {
        return !!setup.rules.find(rule => (rule.variant === variant ? !rule.invert : rule.invert));
    }

    /**
     * Parse string rule to rule setup
     */
    private strToSetup(str: string): ISetup | undefined {
        if (!this.setupMap.has(str)) {
            this.setupMap.set(str, this.parseRule(str));
        }
        return this.setupMap.get(str);
    }

    private parseRule(str: string): ISetup | undefined {
        const parts = str.match(/\(([^\)]+)\)(.*)/);
        if (parts) {
            const rules = parts[2]
                .toUpperCase()
                .split(',')
                .map(i => i.trim())
                .map(i => {
                    const invert = i[0] == '!';
                    return {
                        invert: invert,
                        variant: invert ? i.substr(1) : i
                    };
                });
            return {
                exp: parts[1].trim(),
                rules: rules
            };
        }
        return undefined;
    }
}
